/**
 * Rider model (SPEC §6.1), integrated at 20 Hz:
 *   m·dv/dt = η·P/v − [ m·g·(sinθ + Crr·cosθ) + ½·ρ·CdA_eff·v² ]
 * with the low-speed form v = max(v, 0.5) in the propulsion term. Coasting and stopping are allowed (v ≥ 0).
 * Game speed always comes from measured power, never from trainer-reported speed.
 */
import { airDensity, DEFAULT_CRR, ETA, G, gradeToAngle } from './constants';

export interface RiderParams {
  massKg: number;
  cda: number;
  crr?: number;
  eta?: number;
}

export interface RiderEnv {
  gradePct: number;
  altitudeM: number;
  /** Draft reduction 0..0.4 (SPEC §9.4). */
  draft?: number;
}

export function resistanceForce(v: number, p: RiderParams, env: RiderEnv): number {
  const theta = gradeToAngle(env.gradePct);
  const crr = p.crr ?? DEFAULT_CRR;
  const rho = airDensity(env.altitudeM);
  const cdaEff = p.cda * (1 - (env.draft ?? 0));
  return p.massKg * G * (Math.sin(theta) + crr * Math.cos(theta)) + 0.5 * rho * cdaEff * v * v;
}

export function acceleration(v: number, powerW: number, p: RiderParams, env: RiderEnv): number {
  const eta = p.eta ?? ETA;
  const vEff = Math.max(v, 0.5);
  const propulsion = (eta * Math.max(0, powerW)) / vEff;
  return (propulsion - resistanceForce(v, p, env)) / p.massKg;
}

/** One integration step. Returns the new speed (m/s, ≥ 0). */
export function stepSpeed(v: number, powerW: number, dt: number, p: RiderParams, env: RiderEnv): number {
  // Sub-step for stability when dt is large (background-tab throttling).
  const n = Math.max(1, Math.ceil(dt / 0.05));
  const h = dt / n;
  let s = v;
  for (let i = 0; i < n; i++) {
    s = Math.max(0, s + acceleration(s, powerW, p, env) * h);
  }
  return s;
}

/** Numeric steady-state speed for constant power (bisection on η·P = F(v)·v). */
export function steadyStateSpeed(powerW: number, p: RiderParams, env: RiderEnv): number {
  const eta = p.eta ?? ETA;
  const f = (v: number) => eta * powerW - resistanceForce(v, p, env) * v;
  // On steep descents the rider accelerates without power: find where net force is zero.
  let lo = 0;
  let hi = 40;
  if (f(hi) > 0) return hi;
  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    if (f(mid) > 0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}
